import { Component, type ErrorInfo, type ReactNode } from "react";
import css from "./ErrorBoundary.module.css";

interface Props {
	children: ReactNode;
	/** Optional custom fallback. If omitted, a minimal inline panel is shown. */
	fallback?: (error: Error, reset: () => void) => ReactNode;
	/** A change clears a caught error. The Shell passes the location key, so a
	 *  crash on one page no longer outlives navigating away from it: the
	 *  sidebar and Back used to keep showing this panel over every page the
	 *  URL moved to, until "Try again" or a reload. A `key` would do the same
	 *  by remounting the whole route subtree on every navigation, which would
	 *  also reset pages that deliberately survive sibling-route changes. */
	resetKey?: string;
}

interface State {
	error: Error | null;
}

/**
 * Catches render-time errors in a subtree and shows a recoverable fallback
 * instead of a blank screen. Mount one at the page level (around each route
 * element) so a single broken component does not kill the shell.
 */
export class ErrorBoundary extends Component<Props, State> {
	state: State = { error: null };

	static getDerivedStateFromError(error: Error): State {
		return { error };
	}

	componentDidCatch(error: Error, info: ErrorInfo) {
		// Best-effort structured log; kept non-fatal if console is unavailable.
		try {
			console.error("[ErrorBoundary]", error, info);
		} catch {
			/* ignore */
		}
	}

	componentDidUpdate(prev: Props) {
		if (this.state.error && prev.resetKey !== this.props.resetKey) this.reset();
	}

	reset = () => this.setState({ error: null });

	render() {
		const { error } = this.state;
		if (!error) return this.props.children;
		if (this.props.fallback) return this.props.fallback(error, this.reset);
		return (
			<div role="alert" className={css.panel}>
				<h2 className={css.heading}>
					This page couldn't load
				</h2>
				<p className={css.description}>
					A rendering error occurred. Try refreshing the page or going back.
					If the problem persists, check the server logs for details.
				</p>
				<details className={css.details}>
					<summary className={css.summary}>Technical details</summary>
					<pre className={css.pre}>
						{error.message}
					</pre>
				</details>
				<div className={css.actions}>
					<button
						type="button"
						onClick={this.reset}
						className={css.primaryBtn}
					>
						Try again
					</button>
					<button
						type="button"
						onClick={() => window.location.reload()}
						className={css.secondaryBtn}
					>
						Reload page
					</button>
				</div>
			</div>
		);
	}
}
